import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Wallet, DollarSign, TrendingUp, AlertCircle, CheckCircle, XCircle, Shield } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { useToast } from '../hooks/use-toast';
import WalletConnect from '../components/WalletConnect';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CRELoanApplication = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [property, setProperty] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [applicationResult, setApplicationResult] = useState(null);
  
  // Form data
  const [cryptoType, setCryptoType] = useState('BTC');
  const [cryptoAmount, setCryptoAmount] = useState(1);
  const [ltvRatio, setLtvRatio] = useState(50);
  const [btcPrice, setBtcPrice] = useState(94707);
  const [ethPrice, setEthPrice] = useState(3245.67);
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');

  useEffect(() => {
    fetchProperty();
    fetchCryptoPrices();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cre/cre-properties/${id}`);
      if (response.data.success) {
        setProperty(response.data.property);
      }
    } catch (error) {
      console.error('Error fetching property:', error);
      toast({
        title: 'Error',
        description: 'Failed to load property details.',
        variant: 'destructive'
      });
    }
  };

  const fetchCryptoPrices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/crypto/prices`);
      if (response.data.success) {
        setBtcPrice(response.data.prices.BTC.price);
        setEthPrice(response.data.prices.ETH.price);
      }
    } catch (error) {
      console.error('Error fetching prices:', error);
    }
  };

  const currentPrice = cryptoType === 'BTC' ? btcPrice : ethPrice;
  const collateralValue = cryptoAmount * currentPrice;
  const loanAmount = (collateralValue * ltvRatio) / 100;
  const canProceed = property ? loanAmount >= (property.property_value * 0.3) : false;

  const handleConnectWallet = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const mockAddress = `0x${Math.random().toString(16).substr(2, 40)}`;
      setWalletAddress(mockAddress);
      setWalletConnected(true);
      
      toast({
        title: 'Wallet Connected',
        description: 'Your Coinbase wallet has been connected successfully.',
      });
    } catch (error) {
      toast({
        title: 'Connection Failed',
        description: 'Failed to connect wallet. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitApplication = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast({
          title: 'Login Required',
          description: 'Please login to submit an application.',
          variant: 'destructive'
        });
        navigate('/login');
        return;
      }
      
      // Use the enhanced loan endpoint with auto-approval
      const response = await axios.post(`${BACKEND_URL}/api/loans/apply`, {
        property_id: property.id,
        loan_amount: loanAmount,
        crypto_type: cryptoType,
        crypto_amount: cryptoAmount,
        wallet_address: walletAddress,
        term_months: 24
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const status = response.data.status;
        
        if (status === 'approved') {
          toast({
            title: 'Application Approved! 🎉',
            description: 'Your loan has been auto-approved based on your collateral ratio.',
          });
        } else if (status === 'pending_review') {
          toast({
            title: 'Application Under Review',
            description: 'Your application requires manual review. You will be notified within 2-3 business days.',
          });
        } else {
          toast({
            title: 'Application Submitted',
            description: response.data.message || 'Your loan application has been submitted.',
            variant: 'destructive'
          });
        }
        
        // Show approval details
        setApplicationResult(response.data);
        setStep(4);
      }
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error.response?.data?.detail || 'Failed to submit application.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate collateral ratio for display
  const collateralRatio = loanAmount > 0 ? ((collateralValue / loanAmount) * 100).toFixed(1) : 0;

  if (!property) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1e293b] to-[#0f172a] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1e293b] to-[#0f172a]">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">Apply for Bitcoin-Backed Loan</h1>
            <p className="text-gray-400">Complete your application to acquire {property.name}</p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                step >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-400'
              }`}>
                {step > 1 ? <CheckCircle className="h-5 w-5" /> : '1'}
              </div>
              <div className={`h-1 w-16 ${
                step >= 2 ? 'bg-blue-500' : 'bg-gray-700'
              }`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                step >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-400'
              }`}>
                {step > 2 ? <CheckCircle className="h-5 w-5" /> : '2'}
              </div>
              <div className={`h-1 w-16 ${
                step >= 3 ? 'bg-blue-500' : 'bg-gray-700'
              }`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                step >= 3 ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-400'
              }`}>
                {step > 3 ? <CheckCircle className="h-5 w-5" /> : '3'}
              </div>
              <div className={`h-1 w-16 ${
                step >= 4 ? 'bg-blue-500' : 'bg-gray-700'
              }`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                step >= 4 ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-400'
              }`}>
                {step >= 4 ? <CheckCircle className="h-5 w-5" /> : '4'}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Form */}
            <div>
              {step === 1 && (
                <div className="bg-slate-800/50 border border-blue-500/30 rounded-lg p-8">
                  <h2 className="text-2xl font-bold text-white mb-6">Collateral Configuration</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Select Cryptocurrency</label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          onClick={() => setCryptoType('BTC')}
                          className={`p-4 rounded-lg border transition-all ${
                            cryptoType === 'BTC'
                              ? 'border-blue-500 bg-blue-500/20 text-white'
                              : 'border-blue-500/30 text-gray-400 hover:border-blue-400'
                          }`}
                        >
                          <div className="font-semibold">Bitcoin (BTC)</div>
                          <div className="text-sm">${btcPrice.toLocaleString()}</div>
                        </button>
                        <button
                          onClick={() => setCryptoType('ETH')}
                          className={`p-4 rounded-lg border transition-all ${
                            cryptoType === 'ETH'
                              ? 'border-blue-500 bg-blue-500/20 text-white'
                              : 'border-blue-500/30 text-gray-400 hover:border-blue-400'
                          }`}
                        >
                          <div className="font-semibold">Ethereum (ETH)</div>
                          <div className="text-sm">${ethPrice.toLocaleString()}</div>
                        </button>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-gray-300">Amount of {cryptoType}</label>
                        <span className="text-white font-semibold">{cryptoAmount} {cryptoType}</span>
                      </div>
                      <Slider 
                        value={[cryptoAmount]} 
                        onValueChange={(value) => setCryptoAmount(value[0])}
                        min={0.1}
                        max={1000}
                        step={0.5}
                        className="my-4"
                      />
                      <div className="text-sm text-gray-400">
                        Collateral Value: ${collateralValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-gray-300">Loan-to-Value Ratio</label>
                        <span className="text-white font-semibold">{ltvRatio}%</span>
                      </div>
                      <Slider 
                        value={[ltvRatio]} 
                        onValueChange={(value) => setLtvRatio(value[0])}
                        min={20}
                        max={150}
                        step={5}
                        className="my-4"
                      />
                      <div className="text-sm text-gray-400">
                        Loan Amount: ${loanAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </div>
                    </div>

                    {!canProceed && (
                      <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4 flex items-start">
                        <AlertCircle className="h-5 w-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-yellow-200">
                          Your loan amount must cover at least 30% of the property value (${(property.property_value * 0.3).toLocaleString()}) to proceed.
                        </div>
                      </div>
                    )}

                    <Button 
                      onClick={() => setStep(2)}
                      disabled={!canProceed}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white py-6"
                    >
                      Continue to Wallet Connection
                    </Button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="bg-slate-800/50 border border-blue-500/30 rounded-lg p-8">
                  <h2 className="text-2xl font-bold text-white mb-6">Connect Wallet</h2>
                  
                  {!walletConnected ? (
                    <div className="space-y-6">
                      <WalletConnect 
                        onConnect={(address) => {
                          setWalletAddress(address);
                          setWalletConnected(true);
                        }}
                        onDisconnect={() => {
                          setWalletAddress('');
                          setWalletConnected(false);
                        }}
                      />
                      
                      <div className="flex gap-4">
                        <Button 
                          onClick={() => setStep(1)}
                          variant="outline"
                          className="flex-1 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white py-6"
                        >
                          Back
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <Wallet className="h-6 w-6 text-green-400 mr-3" />
                          <span className="text-green-400 font-semibold">Wallet Connected</span>
                        </div>
                        <div className="text-sm text-gray-300">
                          Address: <span className="text-white font-mono">{walletAddress.slice(0, 10)}...{walletAddress.slice(-8)}</span>
                        </div>
                      </div>

                      <div className="bg-blue-900/20 rounded-lg p-4">
                        <p className="text-gray-300 text-sm">
                          Your {cryptoAmount} {cryptoType} will be securely held as collateral. You'll maintain ownership and can reclaim it after loan repayment.
                        </p>
                      </div>

                      <div className="flex gap-4">
                        <Button 
                          onClick={() => setStep(1)}
                          variant="outline"
                          className="flex-1 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white py-6"
                        >
                          Back
                        </Button>
                        <Button 
                          onClick={() => setStep(3)}
                          className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-6"
                        >
                          Review Application
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {step === 3 && (
                <div className="bg-slate-800/50 border border-blue-500/30 rounded-lg p-8">
                  <h2 className="text-2xl font-bold text-white mb-6">Review & Submit</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between py-3 border-b border-blue-500/30">
                      <span className="text-gray-400">Property</span>
                      <span className="text-white font-semibold">{property.name}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-blue-500/30">
                      <span className="text-gray-400">Property Value</span>
                      <span className="text-white font-semibold">${property.property_value.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-blue-500/30">
                      <span className="text-gray-400">Collateral</span>
                      <span className="text-white font-semibold">{cryptoAmount} {cryptoType}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-blue-500/30">
                      <span className="text-gray-400">Collateral Value</span>
                      <span className="text-white font-semibold">${collateralValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-blue-500/30">
                      <span className="text-gray-400">Collateral Ratio</span>
                      <span className={`font-semibold ${parseFloat(collateralRatio) >= 200 ? 'text-green-400' : parseFloat(collateralRatio) >= 150 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {collateralRatio}%
                      </span>
                    </div>
                    <div className="flex justify-between py-3 text-lg font-bold">
                      <span className="text-white">Loan Amount</span>
                      <span className="text-blue-400">${loanAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>

                  {/* Collateral Ratio Indicator */}
                  <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">Approval Threshold</span>
                      <span className={`${parseFloat(collateralRatio) >= 150 ? 'text-green-400' : 'text-red-400'}`}>
                        {parseFloat(collateralRatio) >= 200 ? '✓ Auto-Approve' : parseFloat(collateralRatio) >= 150 ? '✓ Meets Minimum' : '✗ Below Minimum'}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-600 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${parseFloat(collateralRatio) >= 200 ? 'bg-green-500' : parseFloat(collateralRatio) >= 150 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.min(100, parseFloat(collateralRatio) / 3)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>0%</span>
                      <span>150% Min</span>
                      <span>200% Auto</span>
                      <span>300%</span>
                    </div>
                  </div>

                  <div className="bg-blue-900/20 rounded-lg p-4 mb-6">
                    <p className="text-gray-300 text-sm">
                      By submitting this application, you agree to our terms and conditions. Your collateral will be held securely until loan repayment.
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      onClick={() => setStep(2)}
                      variant="outline"
                      className="flex-1 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white py-6"
                    >
                      Back
                    </Button>
                    <Button 
                      onClick={handleSubmitApplication}
                      disabled={loading}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-6"
                      data-testid="submit-loan-btn"
                    >
                      {loading ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  </div>
                </div>
              )}

              {step === 4 && applicationResult && (
                <div className={`rounded-lg p-8 ${
                  applicationResult.status === 'approved' 
                    ? 'bg-green-900/30 border border-green-500/50' 
                    : applicationResult.status === 'pending_review'
                      ? 'bg-yellow-900/30 border border-yellow-500/50'
                      : 'bg-red-900/30 border border-red-500/50'
                }`}>
                  <div className="text-center mb-6">
                    {applicationResult.status === 'approved' ? (
                      <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                    ) : applicationResult.status === 'pending_review' ? (
                      <Shield className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
                    ) : (
                      <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
                    )}
                    
                    <h2 className="text-2xl font-bold text-white mb-2">
                      {applicationResult.status === 'approved' 
                        ? 'Loan Approved!' 
                        : applicationResult.status === 'pending_review'
                          ? 'Under Review'
                          : 'Application Not Approved'}
                    </h2>
                    <p className="text-gray-300">{applicationResult.message}</p>
                  </div>

                  {/* Evaluation Details */}
                  {applicationResult.evaluation && (
                    <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
                      <h3 className="text-lg font-semibold text-white mb-4">Evaluation Details</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-gray-400 text-sm">Collateral Ratio</span>
                          <div className="text-xl font-bold text-green-400">{applicationResult.evaluation.collateral_ratio}%</div>
                        </div>
                        <div>
                          <span className="text-gray-400 text-sm">Interest Rate</span>
                          <div className="text-xl font-bold text-blue-400">{applicationResult.evaluation.interest_rate}%</div>
                        </div>
                        <div>
                          <span className="text-gray-400 text-sm">Monthly Payment</span>
                          <div className="text-xl font-bold text-white">${applicationResult.evaluation.monthly_payment?.toLocaleString()}</div>
                        </div>
                        <div>
                          <span className="text-gray-400 text-sm">Total Repayment</span>
                          <div className="text-xl font-bold text-white">${applicationResult.evaluation.total_repayment?.toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Next Steps */}
                  {applicationResult.next_steps && (
                    <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
                      <h3 className="text-lg font-semibold text-white mb-3">Next Steps</h3>
                      <ol className="space-y-2">
                        {applicationResult.next_steps.map((step, i) => (
                          <li key={i} className="flex items-start gap-3 text-gray-300">
                            <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-sm flex items-center justify-center flex-shrink-0">
                              {i + 1}
                            </span>
                            {step}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Suggestions for rejected */}
                  {applicationResult.suggestions && (
                    <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
                      <h3 className="text-lg font-semibold text-white mb-3">Suggestions</h3>
                      <ul className="space-y-2 text-gray-300">
                        {applicationResult.suggestions.map((suggestion, i) => (
                          <li key={i}>• {suggestion}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex gap-4">
                    <Button 
                      onClick={() => navigate('/dashboard')}
                      variant="outline"
                      className="flex-1 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white py-6"
                    >
                      Back to Dashboard
                    </Button>
                    <Button 
                      onClick={() => {
                        setStep(1);
                        setApplicationResult(null);
                      }}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-6"
                    >
                      New Application
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Property Summary */}
            <div className="bg-slate-800/50 border border-blue-500/30 rounded-lg p-8 h-fit">
              <h3 className="text-xl font-bold text-white mb-4">Property Summary</h3>
              <h4 className="text-lg font-semibold text-white mb-2">{property.name}</h4>
              <p className="text-gray-400 text-sm mb-4">{property.address}</p>
              <div className="space-y-2 border-t border-blue-500/30 pt-4">
                <div className="flex justify-between">
                  <span className="text-gray-400 text-sm">Property Value</span>
                  <span className="text-white font-semibold">${property.property_value.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400 text-sm">Size</span>
                  <span className="text-white font-semibold">{property.size_sqft.toLocaleString()} SF</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400 text-sm">Type</span>
                  <span className="text-white font-semibold">{property.property_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400 text-sm">Cap Rate</span>
                  <span className="text-white font-semibold">{property.cap_rate}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400 text-sm">Occupancy</span>
                  <span className="text-white font-semibold">{property.occupancy_rate}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CRELoanApplication;
